<!-- MobileBlocked.vue -->

<script setup>
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { registerMobileWaitlist } from "@/services/mobileWaitlistService";

const router = useRouter();

const contactType = ref("email"); // 'email' | 'whatsapp'
const contactValue = ref("");

const isSending = ref(false);
const errorMessage = ref("");
const successMessage = ref("");

const registeredCounts = ref({
  email: 0,
  whatsapp: 0,
});

function handleGoHome() {
  router.push({ name: "PublicHome" });
}

const contactLabel = computed(() =>
  contactType.value === "email" ? "Seu e-mail" : "Seu WhatsApp"
);

const contactPlaceholder = computed(() =>
  contactType.value === "email" ? "nome@dominio.com" : "81999999999"
);

const currentRegisteredCount = computed(
  () => registeredCounts.value[contactType.value] || 0
);

const isCurrentTypeAlreadyRegistered = computed(
  () => currentRegisteredCount.value >= 1
);

const isContactInputDisabled = computed(
  () => isSending.value || isCurrentTypeAlreadyRegistered.value
);

const submitButtonLabel = computed(() => {
  if (isSending.value) return "Enviando...";

  if (isCurrentTypeAlreadyRegistered.value) {
    return contactType.value === "email"
      ? "E-mail já registrado"
      : "WhatsApp já registrado";
  }

  return "Quero ser avisado";
});

const currentRegisteredMessage = computed(() =>
  contactType.value === "email"
    ? "Você já registrou um e-mail para receber o aviso."
    : "Você já registrou um WhatsApp para receber o aviso."
);

function normalizePhone(value) {
  return String(value || "").replace(/\D/g, "");
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function isValidWhatsapp(value) {
  const digits = normalizePhone(value);
  return digits.length >= 10 && digits.length <= 13;
}

function onInputContact(event) {
  if (contactType.value !== "whatsapp") return;

  const onlyDigits = event.target.value.replace(/\D/g, "");
  contactValue.value = onlyDigits;
}

function onKeydownWhatsapp(event) {
  if (contactType.value !== "whatsapp") return;

  const allowedKeys = [
    "Backspace",
    "Delete",
    "Tab",
    "ArrowLeft",
    "ArrowRight",
    "Home",
    "End",
  ];

  if (allowedKeys.includes(event.key)) return;

  if (!/^\d$/.test(event.key)) {
    event.preventDefault();
  }
}

function clearFeedback() {
  errorMessage.value = "";
  successMessage.value = "";
}

function selectType(newType) {
  contactType.value = newType;
  contactValue.value = "";
  clearFeedback();
}

async function handleSubmit() {
  clearFeedback();

  if (isCurrentTypeAlreadyRegistered.value) {
    errorMessage.value =
      contactType.value === "email"
        ? "Você já registrou um e-mail."
        : "Você já registrou um WhatsApp.";
    return;
  }

  const rawValue = String(contactValue.value || "").trim();

  if (!rawValue) {
    errorMessage.value =
      contactType.value === "email"
        ? "Informe seu e-mail."
        : "Informe seu WhatsApp.";
    return;
  }

  if (contactType.value === "email" && !isValidEmail(rawValue)) {
    errorMessage.value = "Informe um e-mail válido.";
    return;
  }

  if (contactType.value === "whatsapp" && !isValidWhatsapp(rawValue)) {
    errorMessage.value = "Informe um número de WhatsApp válido.";
    return;
  }

  const payload = {
    type: contactType.value,
    value:
      contactType.value === "email" ? rawValue : normalizePhone(rawValue),
  };

  try {
    isSending.value = true;

    await registerMobileWaitlist(payload);

    registeredCounts.value[payload.type] = 1;

    successMessage.value =
      "Contato registrado com sucesso. Avisaremos quando o acesso no celular estiver disponível.";
    contactValue.value = "";
  } catch (err) {
    console.error("Erro ao registrar contato para liberação mobile:", err);

    errorMessage.value =
      err?.response?.data?.message ||
      err?.response?.data?.title ||
      "Ocorreu um erro ao registrar seu contato. Tente novamente.";
  } finally {
    isSending.value = false;
  }
}
</script>

<template>
  <div class="page-container">
    <div class="flex justify-center w-full mb-4">
      <span class="flex items-center gap-1">
        <img
          src="/litterae_pena_fundo_transparente.png"
          alt="Logo Litterae"
          class="w-10 h-10 object-contain"
        />
        <span class="font-bold text-4xl text-slate-900">Litterae</span>
      </span>
    </div>

    <div class="mobile-panel">
      <div class="mobile-body">
        <div class="content-block">
          <h1 class="title">O Litterae está disponível apenas em computadores</h1>

          <p class="description">
            No momento, o acesso à plataforma está disponível apenas em computadores para
            garantir a melhor experiência de leitura, estudo e anotação.
          </p>

          <p class="description description-secondary">
            <button type="button" class="inline-link" @click="handleGoHome">
              Acessar a página inicial
            </button>
          </p>
        </div>

        <div class="notify-box">
          <h2 class="notify-title">Quer ser avisado quando liberar o acesso no celular?</h2>

          <p class="notify-subtitle">
            Deixe seu e-mail ou WhatsApp para receber um aviso quando estiver pronto.
          </p>

          <div
            class="tab-buttons-wrapper"
            role="radiogroup"
            aria-label="Tipo de contato"
          >
            <div class="tab-buttons">
              <button
                type="button"
                class="tab-button"
                :class="{ active: contactType === 'email' }"
                @click="selectType('email')"
                :disabled="isSending"
              >
                E-mail
              </button>

              <button
                type="button"
                class="tab-button"
                :class="{ active: contactType === 'whatsapp' }"
                @click="selectType('whatsapp')"
                :disabled="isSending"
              >
                WhatsApp
              </button>
            </div>
          </div>

          <section class="form-section">
            <div class="form-group">
              <label class="form-label" for="mobile-contact">
                {{ contactLabel }}
              </label>

              <input
                id="mobile-contact"
                :type="contactType === 'email' ? 'email' : 'tel'"
                :inputmode="contactType === 'email' ? 'email' : 'numeric'"
                :placeholder="contactPlaceholder"
                :class="['form-input', { 'input-error': errorMessage }]"
                :value="contactValue"
                :disabled="isContactInputDisabled"
                @input="
                  contactType === 'whatsapp'
                    ? onInputContact($event)
                    : (contactValue = $event.target.value)
                "
                @keydown="onKeydownWhatsapp"
              />
            </div>

            <p v-if="isCurrentTypeAlreadyRegistered" class="field-success">
              {{ currentRegisteredMessage }}
            </p>

            <p v-if="errorMessage" class="field-error">
              {{ errorMessage }}
            </p>

            <p v-if="successMessage" class="field-success">
              {{ successMessage }}
            </p>
          </section>

          <button
            type="button"
            class="btn btn-lg btn-primary w-full"
            @click="handleSubmit"
            :disabled="isSending || isCurrentTypeAlreadyRegistered"
          >
            <i v-if="isSending" class="fas fa-circle-notch fa-spin mr-2"></i>
            <i
              v-else-if="isCurrentTypeAlreadyRegistered"
              class="fas fa-check mr-2"
            ></i>
            <i v-else class="fas fa-paper-plane mr-2"></i>

            <span>
              {{ submitButtonLabel }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import url("https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.2/css/all.min.css");


/* PAGE / CARD */
.page-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: 100vh;
  padding: 5rem 2rem 2rem;
  background: var(--login-gradient);
}

.mobile-panel {
  width: 100%;
  max-width: 30rem;
  background-color: var(--bg-content);
  border-radius: 20px;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.2);
  overflow: hidden;
  margin-top: 0.5rem;
}

.mobile-body {
  padding: 2rem 1.75rem 1.9rem;
}

.mobile-icon-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: 1.25rem;
}

.mobile-icon-circle {
  width: 4.5rem;
  height: 4.5rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--brand-soft) 0%, var(--info-border) 100%);
  color: #7c3aed;
  box-shadow: inset 0 0 0 1px rgba(124, 58, 237, 0.08);
}

.mobile-icon {
  width: 2rem;
  height: 2rem;
}

.content-block {
  text-align: center;
}

.title {
  font-size: 1.4rem;
  line-height: 1.35;
  font-weight: 700;
  color: var(--text-dark);
  margin-bottom: 0.9rem;
}

.description {
  font-size: 0.97rem;
  line-height: 1.65;
  color: var(--text-light);
  margin: 0;
}

.description-secondary {
  margin-top: 0.9rem;
}

.inline-link {
  background-color: transparent;
  border: none;
  padding: 0;
  font-size: inherit;
  font-family: inherit;
  color: #6d28d9;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  border-radius: 4px;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}

.inline-link:hover {
  background-color: var(--brand-tint);
}

.inline-link:active {
  transform: translateY(1px);
}

.inline-link:focus,
.inline-link:focus-visible {
  outline: none;
  box-shadow: none;
}

/* NOTIFY BOX */
.notify-box {
  margin-top: 1.5rem;
  padding: 1rem;
  border: 1px solid var(--border-color);
  border-radius: 16px;
  background: var(--surface-2);
}

.notify-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-dark);
}

.notify-subtitle {
  margin: 0.45rem 0 0;
  font-size: 0.92rem;
  line-height: 1.55;
  color: var(--text-light);
}

.tab-buttons-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}

.tab-buttons {
  display: inline-flex;
  gap: 0.25rem;
}

.tab-button {
  border: none;
  background-color: transparent;
  padding: 0.35rem 0.9rem;
  margin-right: 0.25rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--text-muted);
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}

.tab-button:hover:not(:disabled) {
  background-color: var(--brand-tint);
}

.tab-button.active {
  background-color: var(--brand-soft);
  color: var(--text-on-brand-soft);
  border: none;
}

.tab-button:active:not(:disabled) {
  transform: translateY(1px);
}

.tab-button:focus,
.tab-button:focus-visible {
  outline: none;
  box-shadow: none;
}

.form-section {
  margin-top: 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.field-error {
  font-size: 0.85rem;
  color: var(--danger-hover);
  font-weight: 600;
  margin-top: 0.25rem;
}

.input-error {
  border-color: var(--danger-hover) !important;
}

.input-error:focus {
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.2) !important;
}

.field-success {
  font-size: 0.8rem;
  color: var(--success-text);
}


.btn-primary {
  margin-top: 0.35rem;
}

@media (max-width: 640px) {
  .page-container {
    padding: 2rem 1rem 1.5rem;
  }

  .mobile-body {
    padding: 1.5rem 1rem 1.2rem;
  }

  .title {
    font-size: 1.22rem;
  }

  .description {
    font-size: 0.93rem;
  }

  .notify-box {
    padding: 0.9rem;
  }
}
</style>